import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

export default function Header() {
  const menu = useRef(null);
  const closeTimer = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => {
    if (!menu.current?.open || closeTimer.current) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    menu.current.classList.add("is-closing");
    closeTimer.current = window.setTimeout(
      () => {
        menu.current?.close();
        menu.current?.classList.remove("is-closing");
        setMenuOpen(false);
        closeTimer.current = null;
      },
      reduced ? 0 : 220,
    );
  }, []);
  const openMenu = (event) => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    menu.current.classList.remove("is-closing");
    menu.current.showModal();
    if (event.detail > 0) menu.current.focus({ preventScroll: true });
    setMenuOpen(true);
  };
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);
  const island = useRef(null);
  const inner = useRef(null);
  const { pathname, hash } = useLocation();
  const [intro, setIntro] = useState(
    () =>
      pathname === "/" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useLayoutEffect(() => {
    const measure = () => {
      const style = getComputedStyle(island.current);
      const width =
        inner.current.getBoundingClientRect().width +
        parseFloat(style.paddingLeft) +
        parseFloat(style.paddingRight) +
        2;
      island.current.style.setProperty("--kw-nav-open-width", `${width}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(inner.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  useEffect(() => {
    if (!intro) return;
    const timer = setTimeout(() => setIntro(false), 1850);
    return () => clearTimeout(timer);
  }, [intro]);
  useEffect(() => {
    closeMenu();
  }, [pathname, hash, closeMenu]);
  return (
    <>
      <header
        ref={island}
        className={`kw-nav-island${intro && pathname === "/" ? " is-intro" : ""}`}
      >
        <div className="kw-nav-inner" ref={inner}>
          <Link
            to="/"
            className="kw-nav-brand"
            aria-label="Karalewich home"
            data-analytics="nav_logo"
            data-analytics-event="nav_click"
          >
            <img
              src="/brand/wordmark.svg"
              alt="Karalewich"
              width="164"
              height="27"
            />
          </Link>
          <nav aria-label="Main navigation">
            <NavLink
              to="/software"
              data-analytics="nav_solutions"
              data-analytics-event="nav_click"
            >
              Software
            </NavLink>
            <NavLink
              to="/ai-automation"
              data-analytics="nav_ai"
              data-analytics-event="nav_click"
            >
              AI & Automation
            </NavLink>
            <NavLink
              to="/about"
              data-analytics="nav_about"
              data-analytics-event="nav_click"
            >
              About
            </NavLink>
          </nav>
          <Link
            to="/contact"
            className="kw-nav-contact"
            data-analytics="nav_contact"
          >
            Let’s talk <ArrowUpRight size={14} />
          </Link>
          <button
            type="button"
            className="kw-nav-menu"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="kw-mobile-menu"
            onClick={openMenu}
          >
            <Menu size={21} />
          </button>
        </div>
      </header>
      <dialog
        id="kw-mobile-menu"
        tabIndex={-1}
        className="kw-island-menu"
        onCancel={(event) => {
          event.preventDefault();
          closeMenu();
        }}
        ref={menu}
        aria-label="Main menu"
        onClick={(event) => {
          if (event.target === event.currentTarget || event.target.closest("a"))
            closeMenu();
        }}
      >
        <div>
          <img src="/brand/wordmark.svg" alt="Karalewich" width="150" />
          <button type="button" aria-label="Close menu" onClick={closeMenu}>
            <X size={22} />
          </button>
        </div>
        <nav aria-label="Mobile navigation">
          {[
            ["Software", "/software"],
            ["AI & Automation", "/ai-automation"],
            ["About", "/about"],
            ["App portfolio", "/apps"],
            ["News", "/news"],
            ["How we work", "/how-we-work"],
            ["Contact us", "/contact"],
          ].map(([name, path]) => (
            <Link
              key={path}
              to={path}
              data-analytics={`mobile_${path.slice(1)}`}
              data-analytics-event="nav_click"
            >
              {name}
              <ArrowUpRight size={20} />
            </Link>
          ))}
        </nav>
      </dialog>
    </>
  );
}
